import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Copy } from 'lucide-react';
import type { LegacyProgram, LegacyRow } from '../shared';
import { decodeLegacyText, errorMessage } from './forms';

type Template = LegacyRow & { emailid: string; title: string };
export type NotificationEditor = { template: Template | null; templates: Template[]; programs: LegacyRow[]; groups: LegacyRow[]; providers: string[]; variables: string[] };
type Values = { title: string; title_provider: string; group_id: string; toaddress: string; ccaddress: string; replytoaddress: string; subject: string; body: string; active: boolean; include_providers_email: boolean; include_locations_email: boolean };
export type NotificationTemplateInput = {
  emailid?: string; title?: string; title_provider?: string; variables?: string;
  group_id: string; toaddress: string; ccaddress: string; replytoaddress: string; subject: string; body: string;
  active?: boolean; include_providers_email?: boolean; include_locations_email?: boolean;
};

type Notice = { id: number; text: string; success: boolean };
const TemplateNotice = ({ notice, remove }: { notice: Notice; remove: (id: number) => void }) => {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  useEffect(() => {
    const show = window.setTimeout(() => setVisible(true), 0);
    const hide = window.setTimeout(() => setClosing(true), 5000);
    return () => { window.clearTimeout(show); window.clearTimeout(hide); };
  }, []);
  useEffect(() => {
    if (!closing) return;
    const timer = window.setTimeout(() => remove(notice.id), 200);
    return () => window.clearTimeout(timer);
  }, [closing, notice.id, remove]);
  return <div className="legacy-notification-notice-wrapper" data-visible={visible && !closing} role={notice.success ? 'status' : 'alert'}>
    <div className="legacy-notification-notice-container"><button id={`legacy-notifications-dismiss-notice-${notice.id}`} type="button" className="legacy-notification-notice" data-success={notice.success} aria-label="Dismiss notification" onClick={() => setClosing(true)}><span>{notice.text.split('\n').map((line, index) => <Fragment key={index}>{index > 0 && <br />}{line}</Fragment>)}</span></button></div>
  </div>;
};

const variableSelected = (value: string[], variable: string) => value.some(item => item.toLowerCase() === variable.trim().toLowerCase());
const initialVariables = (options: string[]) => {
  const selected: string[] = [];
  for (const option of options) {
    const variable = option.trim();
    if (!variable || variableSelected(selected, variable)) continue;
    selected.push(variable);
  }
  return selected;
};

const VariableSelector = ({ options, value, onChange }: { options: string[]; value: string[]; onChange: (value: string[]) => void }) => {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState<number | null>(null);
  const input = useRef<HTMLSpanElement>(null);
  const field = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const placedMenu = useRef<HTMLDivElement | null>(null);
  const positionMenu = useRef<(() => void) | null>(null);
  const focusedAt = useRef(0);
  const keyboardActive = useRef(false);
  const available = options.filter(option => !variableSelected(value, option)).slice(0, 20);
  useLayoutEffect(() => {
    const container = field.current;
    const dropdown = menu.current;
    if (!open || !container || !dropdown) { placedMenu.current = null; return; }
    const initialHeight = () => {
      // Tagify's first placement measures a clone without the field's width.
      const clone = dropdown.cloneNode(true) as HTMLDivElement;
      clone.removeAttribute('id'); clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-initial');
      clone.dataset.placement = 'bottom';
      clone.style.cssText = 'position:fixed;top:-9999px;opacity:0;width:auto;max-width:none;pointer-events:none';
      const buttons = Array.from(dropdown.querySelectorAll('button'));
      clone.querySelectorAll('button').forEach((button, index) => {
        button.removeAttribute('id'); button.tabIndex = -1;
        const style = window.getComputedStyle(buttons[index]);
        ['display', 'font', 'padding', 'margin', 'border', 'box-sizing', 'white-space'].forEach(property => button.style.setProperty(property, style.getPropertyValue(property)));
      });
      document.body.appendChild(clone);
      const height = Math.round(clone.getBoundingClientRect().height - 1);
      clone.remove();
      return height;
    };
    const position = (height = Math.round(dropdown.getBoundingClientRect().height - 1)) => {
      const rect = container.getBoundingClientRect();
      const viewport = document.documentElement.clientHeight || window.innerHeight;
      const above = viewport - rect.bottom < height;
      dropdown.dataset.placement = above ? 'top' : 'bottom';
      dropdown.style.top = `${(above ? Math.floor(rect.top) : Math.ceil(rect.bottom - 1)) - rect.top}px`;
    };
    position(placedMenu.current === dropdown ? undefined : initialHeight());
    placedMenu.current = dropdown;
    const show = dropdown.hasAttribute('data-initial') ? window.setTimeout(() => dropdown.removeAttribute('data-initial'), 0) : undefined;
    const reposition = () => position();
    positionMenu.current = reposition;
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.clearTimeout(show);
      positionMenu.current = null;
      window.removeEventListener('resize', reposition); window.removeEventListener('scroll', reposition, true);
    };
  }, [open, available.length]);
  const show = () => { setOpen(available.length > 0); setHighlighted(available.length > 0 ? 0 : null); };
  const remove = (variable: string) => { onChange(value.filter(item => item !== variable)); setOpen(false); setHighlighted(null); };
  const select = (variable: string, pointer = false) => {
    const selected = variable.trim();
    if (selected && !variableSelected(value, selected)) onChange([...value, selected]);
    setOpen(false); setHighlighted(null);
    if (pointer) keyboardActive.current = false;
    input.current?.focus();
  };
  return <div ref={field} className="legacy-notification-variables" onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
    <div className="legacy-notification-tags">
      {value.map((variable, index) => <span key={variable} className="legacy-notification-tag"><span>{variable}</span><button id={`legacy-notifications-remove-variable-${index}`} type="button" tabIndex={-1} aria-label={`Remove ${variable}`} onClick={event => { remove(variable); event.currentTarget.blur(); }}>×</button></span>)}
      <span ref={input} id="legacy-notifications-variables" role="textbox" tabIndex={0} aria-readonly="true" data-empty={value.length === 0} className="legacy-notification-variable-picker" aria-label="Variables" aria-controls="legacy-notifications-variable-options" aria-activedescendant={open && highlighted !== null ? `legacy-notifications-add-variable-${highlighted}` : undefined} onFocus={() => { focusedAt.current = Date.now(); keyboardActive.current = true; show(); }} onClick={() => {
        keyboardActive.current = true;
        if (Date.now() - focusedAt.current <= 500) return;
        if (open) { setOpen(false); return; }
        show();
      }} onKeyDown={event => {
        if (event.key === 'Escape' && open) { event.stopPropagation(); setOpen(false); return; }
        if (event.key === 'ArrowDown' && !open && keyboardActive.current) { show(); event.preventDefault(); return; }
        if (!open) return;
        if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
          const direction = event.key === 'ArrowDown' ? 1 : -1;
          const index = highlighted ?? -1;
          const next = index < 0 ? (direction === 1 ? 0 : available.length - 1) : (index + direction + available.length) % available.length;
          setHighlighted(next);
          const option = document.getElementById(`legacy-notifications-add-variable-${next}`);
          const list = option?.parentElement;
          if (option && list) list.scrollTop = option.clientHeight + option.offsetTop - list.clientHeight;
          positionMenu.current?.(); event.preventDefault(); return;
        }
        if (event.key === 'Enter') {
          if (highlighted !== null) select(available[highlighted]);
          setOpen(false); event.preventDefault(); return;
        }
        // The retained string whitelist consumes these keys without selecting.
        if (['Tab', 'ArrowRight'].includes(event.key) && highlighted !== null) { event.preventDefault(); return; }
        if (event.key === 'Backspace') { onChange(value.slice(0, -1)); setHighlighted(null); event.preventDefault(); }
      }}>{'\u00a0'}</span>
    </div>
    {open && available.length > 0 && <div ref={menu} id="legacy-notifications-variable-options" className="legacy-notification-variable-options" data-initial="true" role="group" aria-label="Available variables" onMouseOver={event => {
      if (event.target instanceof Element && event.target.closest('.legacy-notification-variable-options-content button')) return;
      setHighlighted(null);
    }} onMouseLeave={() => setHighlighted(null)}>
      <div className="legacy-notification-variable-options-content">{available.map((variable, index) => <button id={`legacy-notifications-add-variable-${index}`} key={`${index}-${variable}`} type="button" aria-label={variable.trim() ? undefined : `Blank variable ${index + 1}`} aria-pressed={highlighted === index} onMouseDown={event => event.preventDefault()} onMouseEnter={() => { setHighlighted(index); positionMenu.current?.(); }} onClick={() => select(variable, true)}>{variable}</button>)}</div>
    </div>}
  </div>;
};

export const NotificationTemplateForm = ({ data, program, create, choose, save, reload }: {
  data: NotificationEditor; program: LegacyProgram; create: boolean; choose: (emailId: string) => void;
  save: (input: NotificationTemplateInput) => Promise<string>; reload: () => Promise<void>;
}) => {
  const template = data.template;
  const initial = (name: string) => decodeLegacyText(template?.[name]);
  const checked = (name: string) => Boolean(template?.[name] && template[name] !== '0');
  const groupId = data.groups.some(group => group.id === initial('notification_group')) ? initial('notification_group') : '';
  const { register, watch, handleSubmit, formState: { errors, isSubmitting } } = useForm<Values>({ defaultValues: {
    title: '', title_provider: '', group_id: groupId, toaddress: initial('toaddress'), ccaddress: initial('ccaddress'),
    replytoaddress: initial('replytoaddress'), subject: initial('subject'), body: initial('body'), active: create || checked('active'),
    include_providers_email: checked('include_providers_email'), include_locations_email: checked('include_locations_email'),
  } });
  const options = data.variables;
  const [variables, setVariables] = useState(() => initialVariables(options));
  const [error, setError] = useState('');
  const [created, setCreated] = useState(false);
  const [notices, setNotices] = useState<Notice[]>([]);
  const noticeId = useRef(0);
  const removeNotice = useCallback((id: number) => setNotices(current => current.filter(notice => notice.id !== id)), []);
  const notify = (text: string, success: boolean) => {
    if (!text) return;
    const id = ++noticeId.current;
    setNotices(current => [{ id, text, success }, ...current]);
  };
  const [copied, setCopied] = useState<number | null>(null);
  const selectedProgram = data.programs.find(item => item.slug === program) || data.programs[0];
  const providerGroup = create && ['3', '4', '5'].includes(selectedProgram?.id || '') && watch('group_id') === '2';
  useEffect(() => {
    if (!created) return;
    const timer = window.setTimeout(() => { void reload().catch(caught => { setCreated(false); setError(errorMessage(caught)); }); }, 2000);
    return () => window.clearTimeout(timer);
  }, [created, reload]);
  useEffect(() => {
    if (copied === null) return;
    const timer = window.setTimeout(() => setCopied(null), 1000);
    return () => window.clearTimeout(timer);
  }, [copied]);
  const group = <select id="legacy-notifications-template-group" {...register('group_id')}><option value="" />{data.groups.map((item, index) => <option key={`${item.id}-${index}`} value={item.id || ''}>{decodeLegacyText(item.title)}{item.sub_title ? ` - ${decodeLegacyText(item.sub_title)}` : ''}</option>)}</select>;
  const form = <form id="legacy-notifications-template-form" noValidate onSubmit={handleSubmit(async values => {
    if (isSubmitting || created) return;
    setError('');
    const input: NotificationTemplateInput = {
      group_id: values.group_id, toaddress: values.toaddress, ccaddress: values.ccaddress, replytoaddress: values.replytoaddress, subject: values.subject, body: values.body,
      active: values.active ? true : undefined, include_providers_email: values.include_providers_email ? true : undefined,
      include_locations_email: values.include_locations_email ? true : undefined,
    };
    if (create) Object.assign(input, { title: values.title, title_provider: values.title_provider, variables: variables.join(',') });
    if (!create) input.emailid = template?.emailid;
    try {
      const response = await save(input);
      if (create) { notify(response, true); setCreated(true); return; }
      await reload();
    } catch (caught) {
      const text = errorMessage(caught);
      if (create) { notify(text, false); return; }
      setError(text);
    }
  })}>
    <table className="legacy-notification-template-fields"><tbody>
      {create && <>
        <tr><td><label htmlFor="legacy-notifications-template-program">Program:</label></td><td><select id="legacy-notifications-template-program" defaultValue={selectedProgram?.id || ''}>{data.programs.map((item, index) => <option key={`${item.id}-${index}`} value={item.id || ''} disabled={item.id !== selectedProgram?.id}>{decodeLegacyText(item.name)}</option>)}</select></td></tr>
        <tr><td><label htmlFor="legacy-notifications-template-group">Group:</label></td><td>{group}</td></tr>
        <tr><td><label htmlFor={providerGroup ? 'legacy-notifications-template-provider' : 'legacy-notifications-template-title'}>{providerGroup ? 'Provider Name:' : 'Template Name:'}</label></td><td>
          <input id="legacy-notifications-template-title" type="text" hidden={providerGroup} {...register('title', { validate: value => {
            if (providerGroup) return true;
            if (!value) return 'Please enter the template name.';
            if (value.length < 3) return 'Name must be at least 3 characters long.';
            if (value.length > 100) return 'Name cannot exceed 100 characters.';
            return true;
          } })} />
          <select id="legacy-notifications-template-provider" hidden={!providerGroup} {...register('title_provider')}><option value="" />{data.providers.map((provider, index) => <option key={`${provider}-${index}`} value={provider}>{provider}</option>)}</select>
          {errors.title && !providerGroup && <label role="alert" className="legacy-notification-template-error">{errors.title.message}</label>}
        </td></tr>
      </>}
      {([['toaddress', 'To:'], ['ccaddress', 'CC:'], ['replytoaddress', 'Reply-to:']] as const).map(([name, label]) => <tr key={name}><td><label htmlFor={`legacy-notifications-template-${name}`}>{label}</label></td><td><textarea id={`legacy-notifications-template-${name}`} className="legacy-notification-address" rows={2} {...register(name)} /></td></tr>)}
      <tr><td><label htmlFor="legacy-notifications-template-subject">Subject:</label></td><td><input id="legacy-notifications-template-subject" type="text" {...register('subject')} /></td></tr>
      <tr><td><label htmlFor="legacy-notifications-template-body">Body:</label></td><td><textarea id="legacy-notifications-template-body" rows={20} cols={95} {...register('body')} /></td></tr>
      {!create && <tr><td><label htmlFor="legacy-notifications-template-group">Notification group:</label></td><td>{group}</td></tr>}
      {create && <tr><td><label htmlFor="legacy-notifications-variables">Variables:</label></td><td><VariableSelector options={options} value={variables} onChange={setVariables} /></td></tr>}
      {([['include_providers_email', 'Include providers'], ['include_locations_email', 'Include locations'], ['active', 'Active']] as const).map(([name, label]) => <tr key={name}>
        <td>{create && <label htmlFor={`legacy-notifications-template-${name}`}>{label}</label>}</td>
        <td><input id={`legacy-notifications-template-${name}`} type="checkbox" {...register(name)} />{!create && <>{' '}<label htmlFor={`legacy-notifications-template-${name}`}>{label}</label></>}</td>
      </tr>)}
      <tr><td>&nbsp;</td><td className="legacy-notification-template-actions"><input id="legacy-notifications-template-save" type="submit" value="   Save   " disabled={isSubmitting || created} /></td></tr>
    </tbody></table>
    {error && <p role="alert">{error}</p>}
  </form>;
  return <><table id="legacy-notifications-template-layout" data-mode={create ? 'create' : 'edit'}><tbody>
    {!create && <tr><td colSpan={2} className="legacy-notification-template-choice"><select id="legacy-notifications-template-select" aria-label="Template" defaultValue={template?.emailid || data.templates[0]?.emailid || ''} onChange={event => choose(event.target.value)}>{data.templates.map((item, index) => <option key={`${item.emailid}-${index}`} value={item.emailid || ''}>{decodeLegacyText(item.title)}</option>)}</select></td></tr>}
    <tr><td>{(create || template) && form}{!create && !template && <p>No template selected.</p>}</td>
      {!create && <td className="legacy-notification-template-variable-list"><p><strong>Available variables:</strong></p>{initial('variables').split(',').map((variable, index) => <div key={`${variable}-${index}`}>
        <button id={`legacy-notifications-copy-variable-${index}`} type="button" aria-label={`Copy ${variable || `variable ${index + 1}`}`} onClick={() => { void navigator.clipboard?.writeText(variable).then(() => setCopied(index)).catch(() => undefined); }}><Copy aria-hidden="true" /></button>{' '}{variable}<span className="legacy-notification-variable-copied" hidden={copied !== index}>Copied!!</span>
      </div>)}<br /><br /><br /><br /><br /><br /></td>}
    </tr>
  </tbody></table>{notices.length > 0 && <div className="legacy-notification-notices">{notices.map(notice => <TemplateNotice key={notice.id} notice={notice} remove={removeNotice} />)}</div>}</>;
};
